import { Component, EventEmitter, Input, Output } from '@angular/core'
import { UiService } from 'src/app/services/ui.service'
import { AccessState } from 'src/app/services/access.service'

@Component({
  selector: 'stacksets-update-instructions',
  templateUrl: './stacksets-update-instructions.component.html',
  styleUrls: ['./stacksets-update-instructions.component.scss'],
  standalone: false,
})
export class StacksetsUpdateInstructionsComponent {
  @Input() sourceStackSetTemplateUrl: string
  @Input() targetStackSetTemplateUrl: string
  @Input() sourceAccountId: string
  @Input() sourceRegion: string
  @Input() targetAccountId: string
  @Input() targetRegion: string

  // Access state inputs
  @Input() sourceStackName: string
  @Input() targetStackName: string
  @Input() sourceManagedByStackSet: boolean = false
  @Input() targetManagedByStackSet: boolean = false

  // Access testing inputs
  @Input() sourceAccessState: AccessState
  @Input() targetAccessState: AccessState
  @Input() sourceTestCount: number = 0
  @Input() targetTestCount: number = 0

  // Output for test access
  @Output() testAccess = new EventEmitter<'source' | 'target'>()

  // Management account ID (if known, passed from parent)
  @Input() managementAccountId: string = ''

  // User-entered values
  managementAccountIdInput: string = ''
  primaryStackSetName: string = ''
  recoveryStackSetName: string = ''

  // Track which commands have been copied
  copiedCommands: Set<string> = new Set()

  constructor(public uiService: UiService) {}

  // Get unique account IDs (avoids showing duplicates when source and target are same account)
  get uniqueAccountIds(): string[] {
    const accounts = new Set<string>()
    if (this.sourceAccountId) accounts.add(this.sourceAccountId)
    if (this.targetAccountId) accounts.add(this.targetAccountId)
    return Array.from(accounts)
  }

  // Copy feedback
  onCopied(commandId: string): void {
    this.copiedCommands.add(commandId)
    setTimeout(() => {
      this.copiedCommands.delete(commandId)
    }, 2000)
  }

  isCopied(commandId: string): boolean {
    return this.copiedCommands.has(commandId)
  }

  // Download command as shell script
  downloadCommand(type: 'primary' | 'recovery'): void {
    const command = type === 'primary' ? this.primaryStackSetCommand : this.recoveryStackSetCommand
    const content = `#!/bin/bash\n\n${command}\n`
    const blob = new Blob([content], { type: 'text/x-sh' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `arpio-stackset-${type}.sh`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Helper for management account placeholder
  get mgmtAccountPlaceholder(): string {
    return this.managementAccountIdInput || this.managementAccountId || '<YOUR-MANAGEMENT-ACCOUNT-ID>'
  }

  // Helpers for StackSet name placeholders
  get primaryStackSetNameValue(): string {
    return this.primaryStackSetName || '<YOUR-PRIMARY-STACKSET-NAME>'
  }

  get recoveryStackSetNameValue(): string {
    return this.recoveryStackSetName || '<YOUR-RECOVERY-STACKSET-NAME>'
  }

  // Check if StackSets are already configured (at least one stack managed by StackSet)
  get stackSetsConfigured(): boolean {
    return this.sourceManagedByStackSet || this.targetManagedByStackSet
  }

  // Check if this is a fresh setup (no StackSets configured at all)
  get isSetupMode(): boolean {
    return !this.sourceManagedByStackSet && !this.targetManagedByStackSet
  }

  // Access testing helpers
  get isTestingSource(): boolean {
    return this.sourceAccessState === AccessState.TESTING
  }

  get isTestingTarget(): boolean {
    return this.targetAccessState === AccessState.TESTING
  }

  get sourceIsLatest(): boolean {
    return this.sourceAccessState === AccessState.SUCCESS
  }

  get targetIsLatest(): boolean {
    return this.targetAccessState === AccessState.SUCCESS
  }

  get sourceRetryText(): string {
    if (!this.sourceTestCount) return ''
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return textOptions[this.sourceTestCount % textOptions.length]
  }

  get targetRetryText(): string {
    if (!this.targetTestCount) return ''
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return textOptions[this.targetTestCount % textOptions.length]
  }

  onTestAccess(scope: 'source' | 'target'): void {
    this.testAccess.emit(scope)
  }

  // ==================== COMMAND GENERATORS ====================

  // Unified command getters that switch between create/update based on mode
  get primaryStackSetCommand(): string {
    if (this.isSetupMode) {
      return this.createPrimaryStackSetCommand
    }
    return this.updatePrimaryStackSetCommand
  }

  get recoveryStackSetCommand(): string {
    if (this.isSetupMode) {
      return this.createRecoveryStackSetCommand
    }
    return this.updateRecoveryStackSetCommand
  }

  // Create commands (for setup mode)
  get createPrimaryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --template-url ${this.sourceStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --permission-model SELF_MANAGED`
  }

  get createRecoveryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --template-url ${this.targetStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --permission-model SELF_MANAGED`
  }

  // Update commands (for update mode)
  get updatePrimaryStackSetCommand(): string {
    return `aws cloudformation update-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --template-url ${this.sourceStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  get updateRecoveryStackSetCommand(): string {
    return `aws cloudformation update-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --template-url ${this.targetStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }
}
